import { useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { authApi } from '@/lib/api/auth.api';
import type { AccessibleApplication, AccessibleApplicationsResponse } from '@/lib/api/types';
import { useAuth } from '@/lib/auth/auth-context';
import { decodeJwtPayload, extractRolesFromClaims } from '@/lib/auth/admin-console-access';
import {
  APPLICATION_MANIFESTS,
  resolveManifestForPath,
  type WorkspaceClientId,
} from './application-manifest';

export interface WorkspaceApplication extends AccessibleApplication {
  readonly badgeLabel: string;
  readonly homePath: string;
}

interface UseWorkspaceApplicationsOptions {
  readonly enabled?: boolean;
}

const ADMIN_CONSOLE_ROLES = ['platform-admin', 'platform-operator', 'auditor'] as const;

function canSeeAdminConsoleApplication(roles: readonly string[]) {
  return ADMIN_CONSOLE_ROLES.some((role) => roles.includes(role));
}

function resolveWorkspaceHomePath(clientId: WorkspaceClientId, permissions: readonly string[]) {
  const manifest = APPLICATION_MANIFESTS[clientId];
  if (clientId !== 'oss-maritime-operations') {
    return manifest.homePath;
  }

  const granted = new Set(permissions.map((permission) => permission.trim().toUpperCase()));
  if (granted.has('OWNER_ACCESS') || granted.has('MARITIME.DASHBOARD.VIEW')) {
    return manifest.homePath;
  }

  for (const group of manifest.navGroups) {
    const firstAllowedItem = group.items.find((item) =>
      !item.requiredPermissions ||
      item.requiredPermissions.every((permission) => granted.has(permission.trim().toUpperCase())),
    );
    if (firstAllowedItem) {
      return firstAllowedItem.to;
    }
  }

  return manifest.homePath;
}

export function applyPlatformAdminWorkspaceAccess(
  applications: readonly WorkspaceApplication[],
  isPlatformAdmin: boolean,
): WorkspaceApplication[] {
  if (!isPlatformAdmin) {
    return [...applications];
  }

  return applications.map((application) => ({
    ...application,
    permissions: [...new Set([...application.permissions, 'OWNER_ACCESS', application.clientId])],
  }));
}

export function useCurrentWorkspaceApplication() {
  const { pathname } = useLocation();
  const routeManifest = resolveManifestForPath(pathname);

  return {
    applicationId: '',
    clientId: routeManifest.clientId,
    displayName: routeManifest.label,
    description: null,
    classification: routeManifest.classification,
    roles: [],
    permissions: [],
    badgeLabel: routeManifest.badgeLabel,
    homePath: routeManifest.homePath,
  };
}

export function useWorkspaceApplications(options: UseWorkspaceApplicationsOptions = {}) {
  const auth = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const currentRouteApplication = useCurrentWorkspaceApplication();
  const enabled = options.enabled ?? true;

  const query = useQuery<AccessibleApplicationsResponse>({
    queryKey: ['workspace-applications', auth.user?.profile.sub ?? 'anonymous'],
    queryFn: authApi.applications,
    enabled: enabled && !auth.isLoading && auth.isAuthenticated,
    staleTime: 60_000,
    retry: false,
  });
  const accessTokenClaims = decodeJwtPayload(auth.user?.access_token);
  const profileClaims = auth.user?.profile as Record<string, unknown> | undefined;
  const roles = extractRolesFromClaims(accessTokenClaims).length > 0
    ? extractRolesFromClaims(accessTokenClaims)
    : extractRolesFromClaims(profileClaims);
  const isPlatformAdmin = roles.includes('platform-admin');
  const showAdminConsoleApplication = canSeeAdminConsoleApplication(roles);

  const applications = useMemo<WorkspaceApplication[]>(() => {
    const resolvedApplications = applyPlatformAdminWorkspaceAccess(
      (query.data?.applications ?? [])
        .filter((application) => application.clientId !== 'admin-bff' || showAdminConsoleApplication)
        .filter((application) => application.clientId in APPLICATION_MANIFESTS)
        .map((application) => ({
          ...application,
          badgeLabel: APPLICATION_MANIFESTS[application.clientId as WorkspaceClientId].badgeLabel,
          homePath: resolveWorkspaceHomePath(application.clientId as WorkspaceClientId, application.permissions),
        })),
      isPlatformAdmin,
    );


    if (isPlatformAdmin) {
      const fallbackApplications: WorkspaceApplication[] = (Object.keys(APPLICATION_MANIFESTS) as WorkspaceClientId[])
        .filter((clientId) => !resolvedApplications.some((application) => application.clientId === clientId))
        .map((clientId) => ({
          applicationId: `platform-admin-${clientId}-fallback`,
          clientId,
          displayName: APPLICATION_MANIFESTS[clientId].label,
          description: `Platform-admin fallback access to ${APPLICATION_MANIFESTS[clientId].label}.`,
          classification: APPLICATION_MANIFESTS[clientId].classification,
          roles: [clientId],
          permissions: ['OWNER_ACCESS', clientId],
          badgeLabel: APPLICATION_MANIFESTS[clientId].badgeLabel,
          homePath: APPLICATION_MANIFESTS[clientId].homePath,
        }));

      resolvedApplications.push(...fallbackApplications);
    }

    if (resolvedApplications.length > 0) {
      return resolvedApplications.sort((left, right) => left.displayName.localeCompare(right.displayName));
    }

    return [];
  }, [
    isPlatformAdmin,
    query.data?.applications,
    showAdminConsoleApplication,
  ]);

  const routeManifest = resolveManifestForPath(pathname);
  const routeClientId = routeManifest.clientId;

  const currentApplication = applications.find((application) => application.clientId === routeClientId) ?? applications[0] ?? currentRouteApplication;

  return {
    ...query,
    applications,
    isUsingManifestFallback: false,
    currentApplication,
    hasApplication(clientId: WorkspaceClientId) {
      return applications.some((application) => application.clientId === clientId);
    },
    selectApplication(clientId: WorkspaceClientId) {
      void navigate(APPLICATION_MANIFESTS[clientId].homePath);
    },
  };
}
